/*
 * Project: OKIT.VCM
 *
 * Copyright 2023 by OKIT GmbH
 * All rights reserved.
 *
 * Diese Software ist urheberrechtlich geschützt.
 */
import { useCallback, useContext, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import CasClient, { constant } from "react-cas-client";
import { CasUserContext } from "../context/casUserContext";
import axios from "axios";
import apiList from "../utils/apiClient.js";
import { authAnonymous } from "../hooks/anonymousApi.js";

/**
 *
 */
const casWrapper = () => {
  const casClient = new CasClient(process.env.REACT_APP_CAS_ENDPOINT, {
    version: constant.CAS_VERSION_3_0,
  });

  casClient._validateTicket = (ticket, resolve, reject) =>
    axios
      .post(apiList.CasClient + ticket, {
        service: casClient.redirectUrl,
      })
      .then((response) => {
        //console.log("response: ",response.data)
        let user = response.data.user;
        let roles = response.data.roles;
        localStorage.setItem("roles", roles);
        casClient._handleSuccessValdiate(resolve, user, null);
      })
      .catch((err) => {
        console.log(err);
      });
  return casClient;
};
const useCas = (attempLoginWithGateway = false) => {
  const navigate = useNavigate();
  const { user, login, logout } = useContext(CasUserContext);
  const [isLoading, setIsLoading] = useState(false);
  const casLoginInFlightRef = useRef(null);
  const casLoginNullInFlightRef = useRef(null);

  // Memoize CAS client to avoid re-creating it on every render.
  const casClient = useMemo(() => casWrapper(), []);

  const attemptCasLogin = useCallback(() => {
    return new Promise((resolve, reject) => {
      casClient
        .auth()
        .then((successRes) => {
          login(successRes.user);
          setIsLoading(false);
          setTimeout(() => {
            navigate("/devices", { replace: true });
          }, 1000);
          resolve(successRes);
        })
        .catch((errorRes) => {
          console.error("errorRes ", errorRes);
          setIsLoading(false);
          setTimeout(() => {
            navigate(errorRes.currentPath, { replace: true });
          }, 1000);
          reject(errorRes);
        });
    });
  }, [casClient, login, navigate]);

  const casByPass = useCallback(() => {
    console.log("Start to use Anonymous mode ...");
    (async function () {
      try {
        await authAnonymous().then((response) => {
          let user = response.user;
          let roles = response.roles;
          let clientPrefix = response.clientPrefix;

          localStorage.setItem("roles", roles);
          localStorage.setItem("clientPrefix", clientPrefix);
          localStorage.setItem("user", user);

          setIsLoading(false);
          navigate("/devices", { replace: true });
        });
      } catch (error) {
        console.error("error ", error);
      }
    })();
  }, [navigate]);

  const casLoginNullClientPrefix = useCallback(() => {
    if (casLoginNullInFlightRef.current) return;

    console.log("Start to use CAS ...");
    const p = attemptCasLogin();
    casLoginNullInFlightRef.current = p;

    p.catch((error) => {
      console.error("error ", error);
    }).finally(() => {
      casLoginNullInFlightRef.current = null;
    });
  }, [attemptCasLogin]);

  // Important: `user.auth` is frontend state and can be stale if the backend session cookie expires.
  // So we always attempt CAS login when `casLogin()` is invoked, but keep it single-flight to prevent duplicates.
  const casLogin = useCallback(() => {
    if (casLoginInFlightRef.current) return;

    console.log("Start to use CAS ...");
    const p = attemptCasLogin(attempLoginWithGateway);
    casLoginInFlightRef.current = p;

    p.catch((error) => {
      console.error("error ", error);
    }).finally(() => {
      casLoginInFlightRef.current = null;
    });
  }, [attemptCasLogin, attempLoginWithGateway]);

  const casLogout = useCallback(() => {
    localStorage.removeItem("zoomLevelLat");
    localStorage.removeItem("zoomLevelLong");
    localStorage.removeItem("clientPrefix");
    localStorage.removeItem("roles");
    localStorage.removeItem("user");
    localStorage.removeItem("zoomLevel");
    localStorage.removeItem("position");
    logout();
    casClient.logout();
  }, [casClient, logout]);

  return useMemo(
    () => ({
      isLoading,
      casLogin,
      casLogout,
      casByPass,
      casLoginNullClientPrefix,
    }),
    [isLoading, casLogin, casLogout, casByPass, casLoginNullClientPrefix]
  );
};

export default useCas;
